// Chrome Built-in AI Detector for Web App
// Detects availability of Chrome AI APIs

export interface AIAvailability {
  timestamp: number;
  promptAPI: {
    status: string;
    canUse: boolean;
    downloading?: boolean;
  };
  summarizer: {
    status: string;
    canUse: boolean;
    downloading?: boolean;
  };
  writer: {
    status: string;
    canUse: boolean;
    downloading?: boolean;
  };
}

export class ChromeAIDetector {
  private static CACHE_KEY = 'chrome_ai_availability';
  private static CACHE_TTL = 86400000; // 24 hours

  /**
   * Check all Chrome AI APIs availability
   */
  static async checkAvailability(): Promise<AIAvailability> {
    const availability: AIAvailability = {
      timestamp: Date.now(),
      promptAPI: await this.checkPromptAPI(),
      summarizer: await this.checkSummarizer(),
      writer: await this.checkWriter()
    };

    // Cache results in localStorage
    localStorage.setItem(this.CACHE_KEY, JSON.stringify(availability));

    return availability;
  }

  /**
   * Get cached availability (refresh if expired)
   */
  static async getCached(): Promise<AIAvailability> {
    const cached = localStorage.getItem(this.CACHE_KEY);

    if (cached) {
      const data: AIAvailability = JSON.parse(cached);
      if (Date.now() - data.timestamp < this.CACHE_TTL) {
        return data;
      }
    }

    return await this.checkAvailability();
  }

  /**
   * Check Prompt API (general LLM) - Only in Chrome 138+
   * Note: May be limited to extensions only
   */
  static async checkPromptAPI() {
    try {
      // @ts-ignore - Chrome AI API
      if (!self.ai || !self.ai.languageModel) {
        return { status: 'unavailable', canUse: false };
      }

      // @ts-ignore
      const availability = await self.ai.languageModel.availability();

      return {
        status: availability,
        canUse: availability === 'readily',
        downloading: availability === 'after-download'
      };
    } catch (error) {
      return { status: 'error', canUse: false };
    }
  }

  /**
   * Check Summarizer API - Available in web apps
   */
  static async checkSummarizer() {
    try {
      // @ts-ignore - Chrome AI API
      if (!self.ai || !self.ai.summarizer) {
        return { status: 'unavailable', canUse: false };
      }

      // @ts-ignore
      const availability = await self.ai.summarizer.availability();

      return {
        status: availability,
        canUse: availability === 'readily',
        downloading: availability === 'after-download'
      };
    } catch (error) {
      return { status: 'error', canUse: false };
    }
  }

  /**
   * Check Writer API - Available in web apps
   */
  static async checkWriter() {
    try {
      // @ts-ignore - Chrome AI API
      if (!self.ai || !self.ai.writer) {
        return { status: 'unavailable', canUse: false };
      }

      // @ts-ignore
      const availability = await self.ai.writer.availability();

      return {
        status: availability,
        canUse: availability === 'readily',
        downloading: availability === 'after-download'
      };
    } catch (error) {
      return { status: 'error', canUse: false };
    }
  }

  /**
   * Check if ANY Chrome AI is available
   */
  static async hasAnyAI(): Promise<boolean> {
    const availability = await this.getCached();
    return (
      availability.promptAPI.canUse ||
      availability.summarizer.canUse ||
      availability.writer.canUse
    );
  }

  /**
   * Log status to console (for debugging)
   */
  static async logStatus(): Promise<AIAvailability> {
    const availability = await this.getCached();

    console.group('🤖 Chrome AI Availability');
    console.log('Prompt API:', availability.promptAPI);
    console.log('Summarizer:', availability.summarizer);
    console.log('Writer:', availability.writer);
    console.groupEnd();

    return availability;
  }

  /**
   * Show notification about AI status
   */
  static async showStatus(): Promise<void> {
    const availability = await this.getCached();
    const hasAI = await this.hasAnyAI();

    if (hasAI) {
      const apis = [];
      if (availability.summarizer.canUse) apis.push('Summarizer');
      if (availability.writer.canUse) apis.push('Writer');
      if (availability.promptAPI.canUse) apis.push('Prompt API');

      console.log(`✅ Chrome AI Available: ${apis.join(', ')}`);
    } else {
      const downloading = [];
      if (availability.summarizer.downloading) downloading.push('Summarizer');
      if (availability.writer.downloading) downloading.push('Writer');
      if (availability.promptAPI.downloading) downloading.push('Prompt API');

      if (downloading.length > 0) {
        console.log(`⏳ Chrome AI Downloading: ${downloading.join(', ')}`);
        console.log('   This may take 10-30 minutes. Check chrome://components');
      } else {
        console.log('❌ Chrome AI Not Available');
        console.log('   Requires Chrome 138+ with flags enabled');
        console.log('   See: chrome://flags/#prompt-api-for-gemini-nano');
      }
    }
  }
}
